const DEFAULT_TIME_ZONE = "America/Sao_Paulo"
const BRAZIL_TIME_ZONE_CITY_LABELS: Record<string, string> = {
  "America/Noronha": "Fernando de Noronha",
  "America/Sao_Paulo": "São Paulo",
  "America/Bahia": "Salvador",
  "America/Fortaleza": "Fortaleza",
  "America/Recife": "Recife",
  "America/Belem": "Belém",
  "America/Manaus": "Manaus",
  "America/Cuiaba": "Cuiabá",
  "America/Porto_Velho": "Porto Velho",
  "America/Boa_Vista": "Boa Vista",
  "America/Rio_Branco": "Rio Branco",
  "America/Araguaina": "Araguaína",
  "America/Maceio": "Maceió",
}

const buildDateFromTimeZoneParts = (parts: Intl.DateTimeFormatPart[]): Date => {
  const getPart = (type: Intl.DateTimeFormatPartTypes): number => {
    const value = parts.find((part) => part.type === type)?.value ?? "0"
    return Number(value)
  }

  const year = getPart("year")

  const month = getPart("month")

  const day = getPart("day")

  const hour = getPart("hour")

  const minute = getPart("minute")

  const second = getPart("second")

  return new Date(year, month - 1, day, hour, minute, second)
}

export const resolveRestaurantTimeZone = (timeZoneId?: string | null): string => {
  const candidate = timeZoneId?.trim()

  if (!candidate) {
    return DEFAULT_TIME_ZONE
  }

  try {
    Intl.DateTimeFormat("pt-BR", { timeZone: candidate })
    return candidate
  } catch {
    return DEFAULT_TIME_ZONE
  }
}

export const getRestaurantNow = (timeZoneId?: string | null): Date => {
  const timeZone = resolveRestaurantTimeZone(timeZoneId)

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })

  return buildDateFromTimeZoneParts(formatter.formatToParts(new Date()))
}

const formatDatePartsInTimeZone = (date: Date, timeZoneId?: string | null): Intl.DateTimeFormatPart[] => {
  const timeZone = resolveRestaurantTimeZone(timeZoneId)

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })

  return formatter.formatToParts(date)
}

export const getDateKeyInTimeZone = (date: Date, timeZoneId?: string | null): string => {
  const parts = formatDatePartsInTimeZone(date, timeZoneId)
  const getPart = (type: Intl.DateTimeFormatPartTypes): string => parts.find((part) => part.type === type)?.value ?? ""

  return `${getPart("year")}-${getPart("month")}-${getPart("day")}`
}

export const formatDateInTimeZone = (
  date: Date | string | null | undefined,
  locale: string,
  timeZoneId?: string | null,
): string => {
  if (!date) {
    return "-"
  }

  const parsedDate = date instanceof Date ? date : new Date(date)

  if (Number.isNaN(parsedDate.getTime())) {
    return "-"
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone: resolveRestaurantTimeZone(timeZoneId),
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsedDate)
}

export const parseUtcDate = (value: Date | string | null | undefined): Date | null => {
  if (!value) {
    return null
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  const trimmed = value.trim()

  const hasTimeZoneDesignator = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)

  const hasTimeComponent = /\d{2}:\d{2}/.test(trimmed)

  const normalized = hasTimeZoneDesignator || !hasTimeComponent ? trimmed : `${trimmed.replace(" ", "T")}Z`

  const parsed = new Date(normalized)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export const formatTimeInTimeZone = (
  date: Date | string | null | undefined,
  locale: string,
  timeZoneId?: string | null,
): string => {
  const parsedDate = parseUtcDate(date)

  if (!parsedDate) {
    return "-"
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone: resolveRestaurantTimeZone(timeZoneId),
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(parsedDate)
}

export const formatDateTimeInTimeZone = (
  date: Date | string | null | undefined,
  locale: string,
  timeZoneId?: string | null,
): string => {
  const parsedDate = parseUtcDate(date)

  if (!parsedDate) {
    return "-"
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone: resolveRestaurantTimeZone(timeZoneId),
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(parsedDate)
}

const toOffsetLabel = (offsetMinutes: number): string => {
  const sign = offsetMinutes >= 0 ? "+" : "-"

  const absolute = Math.abs(offsetMinutes)

  const hours = Math.floor(absolute / 60)

  const minutes = absolute % 60

  return `UTC${sign}${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
}

const getOffsetMinutesForTimeZone = (timeZoneId: string, referenceDate: Date = new Date()): number => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timeZoneId,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })

  const parts = formatter.formatToParts(referenceDate)
  const getPart = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? "0")

  const asUtcFromParts = Date.UTC(
    getPart("year"),
    getPart("month") - 1,
    getPart("day"),
    getPart("hour"),
    getPart("minute"),
    getPart("second"),
  )

  return Math.round((asUtcFromParts - referenceDate.getTime()) / 60000)
}

export const convertRestaurantLocalToUtcIso = (
  date: string,
  time: string,
  timeZoneId?: string | null,
): string | null => {
  const dateParts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim())

  const timeParts = /^(\d{2}):(\d{2})$/.exec(time.trim())

  if (!dateParts || !timeParts) {
    return null
  }

  const wallClockAsUtc = Date.UTC(
    Number(dateParts[1]),
    Number(dateParts[2]) - 1,
    Number(dateParts[3]),
    Number(timeParts[1]),
    Number(timeParts[2]),
  )

  if (Number.isNaN(wallClockAsUtc)) {
    return null
  }

  const timeZone = resolveRestaurantTimeZone(timeZoneId)

  const offsetMinutes = getOffsetMinutesForTimeZone(timeZone, new Date(wallClockAsUtc))

  return new Date(wallClockAsUtc - offsetMinutes * 60000).toISOString()
}

export interface TimeZoneOption {
  value: string
  label: string
  offsetMinutes: number
}

const formatTimeZoneLabel = (timeZoneId: string, offsetMinutes: number): string => {
  const cityLabel = BRAZIL_TIME_ZONE_CITY_LABELS[timeZoneId]

  if (cityLabel) {
    return `${toOffsetLabel(offsetMinutes)} — ${cityLabel} (${timeZoneId})`
  }

  return `${toOffsetLabel(offsetMinutes)} — ${timeZoneId}`
}

export const getWorldwideTimeZoneOptions = (): TimeZoneOption[] => {
  const supportedValuesOf = (Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf
  const listTimeZones = typeof supportedValuesOf === "function" ? supportedValuesOf("timeZone") : [DEFAULT_TIME_ZONE]

  return listTimeZones
    .sort((a, b) => a.localeCompare(b, "pt-BR"))
    .map((timeZoneId) => {
      const normalizedValue = resolveRestaurantTimeZone(timeZoneId)
      const offsetMinutes = getOffsetMinutesForTimeZone(normalizedValue)

      return {
        value: normalizedValue,
        label: formatTimeZoneLabel(normalizedValue, offsetMinutes),
        offsetMinutes,
      }
    })
    .sort((a, b) => b.offsetMinutes - a.offsetMinutes || a.label.localeCompare(b.label, "pt-BR"))
}
